import { useId, useState } from 'react';
import { CaretDown, CaretRight } from '@phosphor-icons/react';
import { useShopifyRefusedOrders } from '@discovr/features/shopify';
import type { ShopifyRefusedOrder } from '@discovr/types';
import { DataTable } from '../components/common/DataTable';
import type { ColumnDef } from '../components/common/DataTable';
import { TruncatedText } from '../components/common/TruncatedText';
import { ShopifyCodeCell } from '../campaign-detail/components/shopifyCouponCells';
import { formatStoreMoney } from '../campaign-detail/components/shopifyCurrency';
import { DISPLAY_DECIMALS } from '../lib/format';
import { PAGE_SIZE_OPTIONS } from '../campaign-detail/useCampaignPageSizeParam';
import {
  PAGED_TABLE_MAX_SIZE,
  usePagedTableUrlState,
  useSnapPastEnd,
} from '../campaign-detail/pagedTableState';

const dateFmt = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

const DASH = <span className="text-muted-foreground">—</span>;

function orderValue(r: ShopifyRefusedOrder): string | null {
  if (r.subtotal === undefined || r.subtotal === null) return null;
  return r.currency
    ? formatStoreMoney(r.subtotal, r.currency)
    : r.subtotal.toFixed(DISPLAY_DECIMALS);
}

const columns: ColumnDef<ShopifyRefusedOrder>[] = [
  {
    id: 'order',
    header: 'Order',
    minWidth: 96,
    cell: (r) => <span className="text-body-sm-medium">{r.order_name || r.order_id}</span>,
  },
  {
    id: 'refused_at',
    header: 'Date',
    minWidth: 112,
    cell: (r) => {
      const d = new Date(r.refused_at);
      return Number.isNaN(d.getTime()) ? (
        DASH
      ) : (
        <span className="text-muted-foreground">{dateFmt.format(d)}</span>
      );
    },
  },
  {
    id: 'code',
    header: 'Code',
    minWidth: 120,
    cell: (r) =>
      r.code ? <ShopifyCodeCell code={r.code} className="text-caption-regular" /> : <span className="text-caption-regular">{DASH}</span>,
  },
  {
    id: 'creator',
    header: 'Creator',
    minWidth: 140,
    cell: (r) => {
      if (r.creator_hidden) return <span className="text-muted-foreground">Hidden creator</span>;
      return r.creator_name ? <TruncatedText text={r.creator_name} /> : DASH;
    },
  },
  {
    id: 'subtotal',
    header: 'Order value',
    minWidth: 112,
    align: 'right',
    cell: (r) => orderValue(r) ?? DASH,
  },
  {
    id: 'reason',
    header: 'Reason',
    minWidth: 220,
    grow: true,
    wrap: true,
    cell: (r) => r.reason_label,
  },
];

/** Orders that used a Discovr code or link but were not credited. Renders
 *  nothing until the first page reports at least one. */
export function ShopifyRefusedOrders({ campaignId }: { campaignId: string }) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const url = usePagedTableUrlState('cv_refused');
  const { data, isLoading, isPlaceholderData, refetch } = useShopifyRefusedOrders(campaignId, {
    page: url.page,
    page_size: url.pageSize,
  });
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;
  useSnapPastEnd(url, data ? { totalPages } : undefined, isPlaceholderData);

  if (!data || data.total === 0) return null;
  const Caret = open ? CaretDown : CaretRight;

  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
        className="text-body-sm-semibold text-foreground flex items-center gap-1.5"
      >
        <Caret size={14} aria-hidden />
        Orders not credited ({data.total})
      </button>
      {open ? (
        <div id={panelId} className="mt-2 space-y-2">
          <p className="text-muted-foreground text-caption-regular">
            Orders from the last 90 days that used a Discovr code or link but were not credited to a
            creator. The reason is shown for each order.
          </p>
          <DataTable<ShopifyRefusedOrder>
            columns={columns}
            data={data.items}
            getRowId={(r) =>
              `${r.order_id}:${r.code}:${r.creator_name ?? ''}:${r.creator_hidden ? 'h' : ''}`
            }
            ariaLabel="Orders not credited"
            className="rounded-lg"
            autoHeight
            loading={isLoading}
            onRetry={() => void refetch()}
            pagination={{
              page: url.page,
              pageSize: url.pageSize,
              total: data.total,
              totalPages,
              onPageChange: url.setPage,
              onPageSizeChange: url.setPageSize,
              pageSizeOptions: PAGE_SIZE_OPTIONS,
              maxPageSize: PAGED_TABLE_MAX_SIZE,
              noun: 'orders',
            }}
          />
        </div>
      ) : null}
    </div>
  );
}
